import {
  AUTUMN,
  SPRING,
  SUMMER,
  WINTER,
  seasonAnd,
} from "../../generator/consts";
import { Icon, IconPaths, PlantIcon } from "../Icon/Icon";
import {
  faHeart,
  faSnowflake,
  faSun,
  faUmbrella,
} from "@fortawesome/free-solid-svg-icons";
import { getRecipeHash, usePinnedRecipees } from "../cookies";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Plant } from "../../generator/model";
import React from "react";
import _ from "lodash";
import { faCanadianMapleLeaf } from "@fortawesome/free-brands-svg-icons";
import { faHeart as farHeart } from "@fortawesome/free-regular-svg-icons";
import { useCookies } from "react-cookie";
import { useHover } from "usehooks-ts";

interface Props {
  plants: Plant[];
  placeholder?: boolean;
  isPinnedRow?: boolean; //the recipee is pinned, and it is "the" pinned row
  isTotal?: boolean;
}

const seasonStyles: React.CSSProperties = {};

export const Recipe = (props: Props) => {
  const {
    plants,
    placeholder = false,
    isPinnedRow = false,
    isTotal = false,
  } = props;
  const scaleFactor = 1; // the number of times the recipee can be repeated on a 3x3 tile
  const growthStageMultiplier = 5; // the number of times nutrients get consumed during the growth cycle
  const unitPerFertilizer = 1; // the number of "units" that get replenished for 1 unit of fertilizer used, a tile can hold a max of 100 units
  const pinnedRecipees = usePinnedRecipees();
  const hoverRef = React.useRef(null);
  const isHover = useHover(hoverRef);

  const seasons = seasonAnd(plants.map((p) => p.seasons));
  const waterConsumption =
    _.sumBy(plants, "water" as keyof Plant) * scaleFactor;
  const compost =
    (_.sum(plants.map((p) => p.compost)) *
      scaleFactor *
      growthStageMultiplier) /
    unitPerFertilizer;
  const manure =
    (_.sum(plants.map((p) => p.manure)) * scaleFactor * growthStageMultiplier) /
    unitPerFertilizer;
  const formula =
    (_.sum(plants.map((p) => p.growthFormula)) *
      scaleFactor *
      growthStageMultiplier) /
    unitPerFertilizer;

  const unitStyle = React.useMemo(
    () =>
      ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        lineHeight: 1,
      } as React.CSSProperties),
    []
  );

  const isPinned =
    isPinnedRow ||
    pinnedRecipees.get().map(getRecipeHash).includes(getRecipeHash(plants));

  return (
    <div
      ref={hoverRef}
      className="d-flex"
      style={{
        alignItems: "center",
        justifyContent: "start",
        color: isTotal ? "white" : "black",
        backgroundColor: isTotal
          ? "#228"
          : isHover
          ? "#aac"
          : isPinnedRow
          ? "#ccc"
          : "#fff",
      }}
    >
      <div style={{ fontSize: "64px", display: "flex" }}>
        <FontAwesomeIcon
          title="Autumn"
          icon={faCanadianMapleLeaf}
          size={"1x"}
          fixedWidth
          style={{
            visibility: seasons & AUTUMN ? "initial" : "hidden",
            ...seasonStyles,
          }}
        />
        <FontAwesomeIcon
          title="Winter"
          icon={faSnowflake}
          size={"1x"}
          fixedWidth
          style={{
            visibility: seasons & WINTER ? "initial" : "hidden",
            ...seasonStyles,
          }}
        />
        <FontAwesomeIcon
          title="Spring"
          icon={faUmbrella}
          size={"1x"}
          fixedWidth
          style={{
            visibility: seasons & SPRING ? "initial" : "hidden",
            ...seasonStyles,
          }}
        />
        <FontAwesomeIcon
          title="Summer"
          icon={faSun}
          size={"1x"}
          fixedWidth-100
          style={{
            visibility: seasons & SUMMER ? "initial" : "hidden",
            ...seasonStyles,
          }}
        />
      </div>
      <div
        className="d-flex"
        style={{
          alignItems: "center",
          justifyContent: "start",
          width: 76 * 11,
          maxWidth: 76 * 11,
          overflowX: "hidden",
          marginRight: 10,
          ...(placeholder ? { visibility: "hidden" } : {}),
        }}
      >
        {isTotal ? (
          <h2 style={{ paddingLeft: 5 }}>Pinned Sum</h2>
        ) : (
          props.plants.map((plant, i) => (
            <div key={plant.name + i} style={{ margin: "0px 5px" }}>
              <PlantIcon name={plant.name} />
            </div>
          ))
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 5,
          width: 300,
        }}
      >
        <div
          style={unitStyle}
          title={`${manure} Units/Arrows of any Manure type Fertilizer`}
        >
          {manure}x
          <Icon size={40} path={IconPaths.fertilizerManure} />
        </div>
        <div
          style={unitStyle}
          title={`${compost} Units/Arrows of any Compost type Fertilizer`}
        >
          {compost}x
          <Icon size={40} path={IconPaths.fertilizerCompost} />
        </div>
        <div
          style={unitStyle}
          title={`${formula} Units/Arrows of any Growth Formula type Fertilizer`}
        >
          {formula}x
          <Icon size={40} path={IconPaths.fertilizerGrowthFormula} />
        </div>
        <div
          style={unitStyle}
          title={`This recipee consumes ${waterConsumption} Units/Arrows of Moisture, when used to fill up a tile completely`}
        >
          {waterConsumption}x
          <Icon frameless size={40} path={IconPaths.moisture} />
        </div>
        <div
          title={`Pin/Unpin Recipee`}
          style={{ cursor: "pointer" }}
          onClick={() => {
            if (isPinned) {
              pinnedRecipees.unpinRecipe(plants);
            } else {
              pinnedRecipees.pinRecpie(plants);
            }
          }}
        >
          {isPinned ? (
            <FontAwesomeIcon icon={faHeart} size={"2x"} fixedWidth />
          ) : (
            <FontAwesomeIcon icon={farHeart} size={"2x"} fixedWidth />
          )}
        </div>
      </div>
    </div>
  );
};
